import { LitElement, PropertyValues } from 'lit';
import { queryAll, property } from 'lit/decorators.js';
import { Constructor } from '../types/common/types';
import { isEmpty } from '@nirvana/shared/basic-types/object';

declare class TextHighlightMixinInterface {
  searchString: string;
}

export const TextHighlightMixin = <SuperClass extends Constructor<LitElement>>(superClass: SuperClass) => {
  class TextHighlight extends superClass {
    @queryAll('[searchable-label]')
    private searchableLabelsElements: HTMLElement[];

    @property({ type: String, attribute: true, reflect: true })
    searchString = '';

    updated(changedProperties: PropertyValues) {
      super.updated(changedProperties);
      if (changedProperties.has('searchString')) {
        this.highlightStringMatches(this.searchString);
      }
    }

    /**
     * This function gets the value of the 'searchable-label' or 'label' attributes on the HTML element and checks them against the 'searchString' parameter. If a match is found, the HTML element is highlighted in the DOM
     */
    private highlightStringMatchesFound(searchString: string, element: HTMLElement) {
      const searchableLabelAttribute: string = element.getAttribute('searchable-label') || '';

      let textContent = '';

      // if the element has a label attribute (i.e. inputs, radios, checkboxes, dropdowns, labeled content) get the label value and fallback to text content for other types of elements
      if (element.hasAttribute('label') && !isEmpty(element.hasAttribute('label'))) {
        textContent = element.getAttribute('label') as string;
      } else {
        textContent = element.innerText;
      }

      /**
       * We need to establish what the 'highlighted' state should look like on the components: if it's a generic CSS style rule or each component should implement its own version of this state.
       **/
      element.removeAttribute('highlighted');
      element.style.cssText = '';

      if (
        searchString &&
        (textContent.toLowerCase().includes(searchString.toLowerCase()) ||
          searchableLabelAttribute.toLowerCase().includes(searchString.toLowerCase()))
      ) {
        element.setAttribute('highlighted', '');
        element.style.cssText = 'border-bottom: 2px solid var(--yellow-layout-200-text-highlight)';
      }
    }

    private async highlightStringMatches(searchString: string) {
      await this.updateComplete;
      if (isEmpty(this.searchableLabelsElements)) {
        return;
      }

      Array.from(new Set(this.searchableLabelsElements)).map(label => {
        this.highlightStringMatchesFound(searchString, label);
      });
    }
  }

  return TextHighlight as Constructor<TextHighlightMixinInterface> & SuperClass;
};
